import React, { useMemo } from "react";
import get from "lodash.get";
import styled from "@emotion/styled";
import { useIntl } from "react-intl";
import { connect } from "react-redux";

import { TouchApp as TouchAppIcon } from "@mui/icons-material";

import Abilities from "services/permissions/abilities";
import ContractUtils from "services/contract/utils";
import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import ParkStatisticsActioner from "services/parkStatistics/actioner";
import ParkStatisticsUtils from "services/parkStatistics/utils";

import ParkkiTheme from "themes/ParkkiTheme";

import Section from "components/Section";
import StatsChart from "components/StatsChart";
import StatsRow from "components/StatsRow";
import { GraphProvider } from "../../../../services/graphs";

const InfoComponent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ParkkiTheme.colors.blue};
  text-align: center;
  margin-top: 70px;
`;
const InfoContainer = styled.div`
  width: 250px;
`;
const StyledTouchAppIcon = styled(TouchAppIcon)`
  font-size: 50px;
  color: ${ParkkiTheme.colors.blue};
  margin-bottom: ${ParkkiTheme.spacings.tiny};
`;

const RecenterContainer = styled.div`
  cursor: pointer;
  text-decoration: underline;
  margin-top: ${ParkkiTheme.spacings.tiny};

  &:hover {
    text-decoration: none;
  }
`;

const styles = {
  statsContainer: {
    display: "flex",
    flexDirection: "row",
  },
};

function ParkStatisticsStatsSection({
  contract,
  currentFilters,
  parkStatistics,
  retrieveAllParkStats,
  selectedAreas,
  setCenterToDefault,
}) {
  const intl = useIntl();

  const generalStatsRow = ParkStatisticsUtils.getGeneralStatsRow();
  const keyNumbersStatsRow = ParkStatisticsUtils.getKeyNumbersStatsRow();
  const controlsStatsRow = ParkStatisticsUtils.getControlsStatsRow();
  const chartStats = ParkStatisticsUtils.getChartStats(parkStatistics);
  const hasError = useMemo(() => {
    return (
      parkStatistics?.general?.error ||
      parkStatistics?.control?.error ||
      parkStatistics?.dailyAttendance?.error
    );
  }, [
    parkStatistics?.general?.error,
    parkStatistics?.control?.error,
    parkStatistics?.dailyAttendance?.error,
  ]);

  const toggleMarkersAndAreas = useMemo(() => {
    return MapUtils.toggleMarkersAndAreas(
      currentFilters?.timestamp,
      ContractUtils.getParkMaxDaysAllowed(contract)
    );
  }, [contract, currentFilters?.timestamp]);

  function getNumberOfSection(stats = []) {
    if (stats === null) return 0;
    return stats?.filter((item) => item.toggle)?.length;
  }

  function handleRecenterMap() {
    setCenterToDefault();
    retrieveAllParkStats();
  }

  if (
    toggleMarkersAndAreas &&
    parkStatistics.map.initialized &&
    Abilities.ability.can("read", "park_stats.areasSelection") &&
    selectedAreas?.length === 0
  ) {
    return null;
  }

  if (
    (toggleMarkersAndAreas &&
      !Abilities.ability.can("read", "park_stats.areasSelection") &&
      selectedAreas?.length === 0) ||
    hasError
  ) {
    const errorKey =
      parkStatistics?.general?.error ||
      parkStatistics?.control?.error ||
      parkStatistics?.dailyAttendance?.error
        ? "parkStatisticsScene.anyResultInArea"
        : "components.selectALayerInfo.selectALayer";

    return (
      <InfoComponent>
        <InfoContainer>
          <StyledTouchAppIcon />
          <div>{intl.messages[errorKey]}</div>
          {hasError && (
            <RecenterContainer onClick={handleRecenterMap}>
              {intl.messages["parkStatisticsScene.recenterOnZone"]}
            </RecenterContainer>
          )}
        </InfoContainer>
      </InfoComponent>
    );
  }

  return (
    <>
      {(getNumberOfSection(generalStatsRow) > 0 ||
        getNumberOfSection(keyNumbersStatsRow) > 0) && (
        <Section title={intl.messages["parkStatisticsScene.sections.general"]}>
          {getNumberOfSection(generalStatsRow) > 0 && (
            <StatsRow
              initialized={parkStatistics.general.initialized}
              stats={generalStatsRow}
              numberOfSection={getNumberOfSection(generalStatsRow)}
              containerStyles={{
                ...styles.statsContainer,
                marginBottom: "16px",
              }}
            />
          )}

          {getNumberOfSection(keyNumbersStatsRow) > 0 && (
            <StatsRow
              initialized={parkStatistics.general.initialized}
              stats={keyNumbersStatsRow}
              numberOfSection={getNumberOfSection(keyNumbersStatsRow)}
              containerStyles={styles.statsContainer}
            />
          )}
        </Section>
      )}

      {getNumberOfSection(controlsStatsRow) > 0 && (
        <Section title={intl.messages["parkStatisticsScene.sections.controls"]}>
          <StatsRow
            initialized={parkStatistics.controls.initialized}
            stats={controlsStatsRow}
            numberOfSection={getNumberOfSection(controlsStatsRow)}
            containerStyles={styles.statsContainer}
          />
        </Section>
      )}

      {get(chartStats, "toggle", false) && (
        <GraphProvider
          timeTitle={ContractUtils.formatTimestamp(
            contract,
            currentFilters?.timestamp
          )}
          label={parkStatistics?.map?.selectedLayers
            ?.map((layer) => layer.long_name)
            .join(", ")}
        >
          <Section title={intl.messages["parkStatisticsScene.sections.graph"]}>
            <StatsChart
              initialized={parkStatistics.dailyAttendance.initialized}
              stats={chartStats}
              graphOptions={ParkStatisticsUtils.getAffluenceChartOptions()}
            />
          </Section>
        </GraphProvider>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  parkStatistics: state.ParkStatistics,
  selectedAreas: get(
    ParkStatisticsUtils.getFilters("areas", state),
    "areas",
    []
  ),
  currentFilters: state.ParkStatistics.filters,
});

const mapDispatchToProps = (dispatch) => ({
  retrieveAllParkStats: () =>
    dispatch(ParkStatisticsActioner.retrieveAllStats()),
  setCenterToDefault: () => dispatch(MapActioner.setCenterToDefault()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ParkStatisticsStatsSection);
